import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import { CommonModal } from 'wsm-common-modal';

import { selectIsListLayout } from '../../utilities/layout';
import { getVehicleObj } from '../../utilities/vehicleTracking';
import ConditionalWrapper from '../ConditionalWrapper';

const badgeName = 'vehicle-card-badge';

const CalloutItem = ({
	badgeClasses = [],
	href = null,
	imageAlt,
	imageSrc,
	imageTitle,
	index,
	target = null,
	tooltip = '',
	uuid = '',
	modalTitle = null,
	modalContent = null
}) => {
	const [isOpenModal, setIsOpenModal] = useState(false);
	const isListLayout = useSelector((state) => selectIsListLayout(state));
	const { widgetName, windowId, deviceType } = useRequestData();

	const isMobile = deviceType === 'MOBILE';
	const shouldShowTooltip =
		!!tooltip && typeof tooltip === 'string' && !isMobile;

	// Generate unique IDs for accessibility
	const popoverId = `popover-${uuid}-${index}`;

	// Helper function to strip HTML tags for accessibility
	const stripHtmlTags = (htmlString) => {
		return htmlString.replace(/<[^>]*>/g, '').trim();
	};

	const ariaLabelValue = useMemo(
		() => (shouldShowTooltip ? stripHtmlTags(tooltip) : imageAlt),
		[shouldShowTooltip, tooltip, imageAlt]
	);

	const toggleOpenModal = () => {
		setIsOpenModal((previous) => !previous);
		trackEvent(widgetName, windowId, {
			element: 'Badge modal open',
			elementCTA: imageTitle,
			vehicle: getVehicleObj(uuid)
		});
	};

	// image element needs a height/width set for svg logo to render correctly
	const isCarfax = badgeClasses.includes('carfax');

	// Helper function to create image with appropriate accessibility attributes
	const img = (
		<img
			src={imageSrc}
			width={isCarfax ? '75px' : undefined}
			height={isCarfax ? 'auto' : '30px'}
			alt={imageAlt}
			title={imageTitle}
			style={{ aspectRatio: 'auto 8 / 3' }}
		/>
	);

	/* eslint-disable react/no-danger */
	const tooltipMarkup = (
		<Popover id={popoverId} role="tooltip">
			<span dangerouslySetInnerHTML={{ __html: tooltip }} />
		</Popover>
	);
	/* eslint-enable react/no-danger */

	const decodedHref = href !== null ? href.replace(/&amp;/g, '&') : '';

	// Render badge element based on data availability
	const renderBadgeElement = () => {
		// If href exists, render as link
		if (href) {
			return (
				<a
					href={decodedHref}
					className={setClassNames([...badgeClasses])}
					target={target}
					onClick={() =>
						trackEvent(widgetName, windowId, {
							element: 'Badge button',
							elementCTA: imageTitle,
							vehicle: getVehicleObj(uuid)
						})
					}
					rel={href.startsWith('http') ? 'nofollow' : undefined}
					aria-label={ariaLabelValue}
				>
					{img}
				</a>
			);
		}

		// If disclaimer exists but no href, render as modal trigger button
		if (modalContent?.trim() !== '') {
			return (
				<button
					type="button"
					className={setClassNames([
						...badgeClasses,
						'btn',
						'btn-link',
						'p-0',
						'border-0',
						'bg-transparent'
					])}
					onClick={toggleOpenModal}
					aria-label={ariaLabelValue}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							toggleOpenModal();
						}
					}}
				>
					{img}
				</button>
			);
		}

		// Default: render just the image (no interaction)
		return img;
	};

	return (
		<>
			<li
				key={imageTitle}
				className={setClassNames([
					badgeName,
					...badgeClasses,
					isListLayout ? 'py-3' : 'p-0',
					!isListLayout && index === 0 ? 'pl-0 mr-2' : 'm-2'
				])}
			>
				<ConditionalWrapper
					condition={shouldShowTooltip}
					wrapper={(children) => (
						<OverlayTrigger
							placement="top"
							overlay={tooltipMarkup}
							trigger="hover"
						>
							{children}
						</OverlayTrigger>
					)}
				>
					{renderBadgeElement()}
				</ConditionalWrapper>
			</li>

			{!href && modalContent?.trim() && (
				<CommonModal
					title={modalTitle}
					isOpen={isOpenModal}
					onClose={toggleOpenModal}
					key="common-modal"
				>
					<div dangerouslySetInnerHTML={{ __html: modalContent }} />
				</CommonModal>
			)}
		</>
	);
};

export default CalloutItem;

CalloutItem.propTypes = {
	badgeClasses: PropTypes.arrayOf(PropTypes.string),
	href: PropTypes.string,
	imageAlt: PropTypes.string,
	imageSrc: PropTypes.string,
	imageTitle: PropTypes.string,
	index: PropTypes.number,
	target: PropTypes.string,
	tooltip: PropTypes.string,
	uuid: PropTypes.string,
	modalTitle: PropTypes.string,
	modalContent: PropTypes.string
};

CalloutItem.defaultProps = {
	badgeClasses: [],
	href: null,
	target: null,
	tooltip: '',
	uuid: ''
};
